import { useState } from 'react'
import * as React from 'react'
import cx from 'classnames'
import { Input } from 'almanac-ui/TextInputField/Input'
import Shared from '../../Shared.module.scss'
import Styles from './PasswordFields.module.scss'
import { String } from 'lodash'

import { CheckThick, CloseThick } from 'almanac-ui/icons'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { useIsOnboardingRedesign } from 'hooks/useIsOnboardingRedesign'
import { EyeClosed, EyeOpen } from 'blaze-ui/icons'
import { IconButton } from 'blaze-ui/IconButton/IconButton'

type Props = {
  errors: any
  submittedWithErrors: boolean
  password: string
  isProcessing: boolean
  passwordSubmitted: boolean
  handleSubmitPassword: (e: React.KeyboardEvent) => void
  passwordLabel?: string
  passwordPlaceholder?: string
  passwordInputRef?: React.RefObject<HTMLInputElement>
  autoFocus?: boolean
  buttonTestId?: String
  handleChange: {
    (e: React.ChangeEvent<any>): void
    <T = string | React.ChangeEvent<any>>(field: T): T extends React.ChangeEvent<any>
      ? void
      : (e: string | React.ChangeEvent<any>) => void
  }
}

const PasswordFields = ({
  errors,
  submittedWithErrors,
  password,
  passwordLabel,
  passwordPlaceholder,
  handleSubmitPassword,
  passwordSubmitted,
  isProcessing,
  passwordInputRef,
  handleChange,
  autoFocus = false,
}: Props) => {
  const isOnboardingRedesign = useIsOnboardingRedesign()
  const hasCapitol = (password: string) => password.match(/[A-Z]/)
  const hasNumber = (password: string) => password.match(/\d+/)
  const hasLength = (password: string) => password.length >= 8
  const [showPassword, setShowPassword] = useState(false)
  return (
    <>
      {/* Create Password Field */}
      <Text variant="label">
        {passwordLabel}
        <p className={Styles.warning}>*</p>
      </Text>
      <div className={Shared.newFieldWrapper}>
        <Input
          name="password"
          id="password"
          size="xl"
          type={showPassword ? 'text' : 'password'}
          placeholder={passwordPlaceholder}
          isValid={!(errors.password && passwordSubmitted)}
          autoComplete="new-password"
          autoFocus={autoFocus}
          onChange={handleChange}
          onKeyDown={handleSubmitPassword}
          ref={passwordInputRef}
          disabled={isProcessing}
        />
        {password && (
          <div className={cx(Shared.showPasswordButton, Styles.showPasswordButton)}>
            {isOnboardingRedesign ? (
              <IconButton
                onPress={() => setShowPassword(!showPassword)}
                variant="tertiary"
                icon={showPassword ? EyeClosed : EyeOpen}
                color="var(--dark-40)"
              />
            ) : (
              <Button onPress={() => setShowPassword(!showPassword)} variant="ghost" ignoreHover>
                <Text variant="smallList" color="var(--dark-40)">
                  {showPassword ? 'Hide' : 'Show'}
                </Text>
              </Button>
            )}
          </div>
        )}
      </div>

      {password && (
        <div
          className={cx(Styles.passwordValidationContainer, {
            [Styles.passwordValidationContainerError]: errors.password && submittedWithErrors,
          })}
        >
          <span>Password must contain at least:</span>
          <div className={Styles.requirementOptions}>
            {[
              {
                validator: (value: string) => hasLength(value),
                message: '8 characters',
              },
              {
                validator: (value: string) => hasNumber(value),
                message: 'One number',
              },
              {
                validator: (value: string) => hasCapitol(value),
                message: 'One capital letter',
              },
            ].map(({ validator, message }, idx) => (
              <div className={Styles.passwordRequirement} key={`validator-${idx}`}>
                {validator(password) ? (
                  <>
                    <CheckThick className={Styles.success} />
                  </>
                ) : (
                  <>
                    <CloseThick className={Styles.error} />
                  </>
                )}
                <span className={Styles.errorText}>{message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  )
}

export default PasswordFields
